// FILE: src/pages/StudentActivityPage.jsx
// "My activity" (plan WP-8, MTO 11.1): the student's own audit trail, newest
// first, grouped by day. Actions and reasons are words from labels.js;
// details are summarised from known fields only (B-23: never
// Object.entries on details that may be null, never JSON). Paging is local
// state (there is no setPage reducer, B-23); the page size lives in the
// slice (setLimit).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from "@mui/material";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { fetchMyActivity, setLimit } from "../features/myActivitySlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { auditActionLabel, auditReasonLabel, humanize, ticketCategoryLabel } from "../i18n/labels";
import { formatDuration } from "../utils/timeFormat";

const PAGE_SIZES = [10, 25, 50];
const CODE_RE = /^[A-Z][A-Z0-9_]+$/;

function iconFor(action = "") {
  if (/^(LOGIN|LOGOUT|OTP|SESSION)/.test(action)) return LoginOutlinedIcon;
  if (/(PASSWORD|CHALLENGE|TOTP|LOCKED|UNLOCKED|IDENTITY)/.test(action)) return ShieldOutlinedIcon;
  if (/^(LEARNING|BREAK|DAILY|COMPLETION|RESOURCE)/.test(action)) return MenuBookOutlinedIcon;
  if (/^SUPPORT/.test(action)) return SupportAgentOutlinedIcon;
  if (/^(PROFILE|CONSENT|USER|INVITE|STUDENT|ENROLMENT|ADMIN|UNTIMED)/.test(action)) return PersonOutlineIcon;
  return HistoryOutlinedIcon;
}

/** "address.postalCode" → "Postal code". */
function fieldLabel(field) {
  const last = String(field).split(".").pop();
  return humanize(last.replace(/([a-z])([A-Z])/g, "$1_$2"));
}

/** Readable details from the fields we know; anything else is left out. */
export function detailText(details) {
  if (!details || typeof details !== "object" || Array.isArray(details)) return "";
  const parts = [];
  if (typeof details.reason === "string" && CODE_RE.test(details.reason)) {
    parts.push(`Reason: ${auditReasonLabel(details.reason)}`);
  }
  if (typeof details.category === "string") parts.push(ticketCategoryLabel(details.category));
  if (Array.isArray(details.changed) && details.changed.length) {
    parts.push(`Changed: ${details.changed.map(fieldLabel).join(", ")}`);
  }
  if (Number.isFinite(details.attemptsRemaining)) {
    parts.push(`${details.attemptsRemaining} ${details.attemptsRemaining === 1 ? "attempt" : "attempts"} left`);
  }
  if (Number.isFinite(details.finalScore)) parts.push(`Score: ${Math.round(details.finalScore)}%`);
  if (Number.isFinite(details.creditedSeconds)) parts.push(`Time counted: ${formatDuration(details.creditedSeconds)}`);
  if (Number.isFinite(details.days)) parts.push(`${details.days} ${details.days === 1 ? "day" : "days"} added`);
  return parts.join(" · ");
}

/** "Chrome on macOS" from a user-agent string; null when there is none. */
export function deviceName(ua) {
  if (!ua || typeof ua !== "string") return null;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : null;
  const os = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Windows/.test(ua)
        ? "Windows"
        : /CrOS/.test(ua)
          ? "ChromeOS"
          : /Mac OS X|Macintosh/.test(ua)
            ? "macOS"
            : /Linux/.test(ua)
              ? "Linux"
              : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || "Other device";
}

function deviceText(log) {
  const device = deviceName(log.userAgent);
  const ip = log.ip || (log.details && typeof log.details === "object" ? log.details.ip : null);
  if (device && ip) return `${device} (IP ${ip})`;
  if (device) return device;
  if (typeof ip === "string" && ip) return `IP ${ip}`;
  return null;
}

function toDate(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function dayKey(d) {
  return d ? d.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "Date unknown";
}

function ActivityItem({ log }) {
  const Icon = iconFor(log.action);
  const d = toDate(log.createdAt);
  const details = detailText(log.details);
  const device = deviceText(log);
  return (
    <Box
      component="li"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "auto minmax(0, 1fr)", sm: "6.5rem auto minmax(0, 1fr)" },
        columnGap: 2,
        rowGap: 0.5,
        alignItems: "start",
        py: 1.75,
        borderTop: "1px solid",
        borderColor: "divider",
        "&:first-of-type": { borderTop: 0, pt: 0.5 }
      }}
    >
      <Typography
        component="time"
        dateTime={d ? d.toISOString() : undefined}
        sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", gridColumn: { xs: "2", sm: "1" }, gridRow: "1" }}
      >
        {d ? d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" }) : "—"}
      </Typography>
      <Box
        aria-hidden
        sx={{
          gridRow: { xs: "1 / span 2", sm: "1 / span 2" },
          width: 36,
          height: 36,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: "zun.primaryTint",
          color: "primary.main"
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, gridColumn: { xs: "2", sm: "3" }, gridRow: { xs: "2", sm: "1 / span 2" } }}>
        <Typography sx={{ fontWeight: 600 }}>{auditActionLabel(log.action)}</Typography>
        {details && <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{details}</Typography>}
        {device && (
          <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>Device: {device}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default function StudentActivityPage() {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?._id);
  const { logs, totalCount, totalPages, limit, loading, error } = useSelector((state) => state.myActivity);
  const [page, setPage] = useState(1);
  const pageSize = PAGE_SIZES.includes(limit) ? limit : PAGE_SIZES[0];
  const list = useMemo(() => (Array.isArray(logs) ? logs : []), [logs]);
  const pages = Math.max(1, Number(totalPages) || 1);

  const load = useCallback(() => {
    if (userId) dispatch(fetchMyActivity({ userId, page, limit: pageSize }));
  }, [dispatch, userId, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Fewer pages than the current one (e.g. after a larger page size): go back.
  useEffect(() => {
    if (!loading && totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [loading, totalPages, page]);

  const groups = useMemo(() => {
    const out = [];
    for (const log of list) {
      const key = dayKey(toDate(log.createdAt));
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(log);
      else out.push({ key, items: [log] });
    }
    return out;
  }, [list]);

  const from = totalCount ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, totalCount || 0);

  let body;
  if (loading) {
    body = <LoadingState variant="table" rows={4} label="Loading your activity…" />;
  } else if (error) {
    body = <ErrorState error={error} title="We couldn't load your activity" onRetry={load} />;
  } else if (list.length === 0) {
    body = (
      <EmptyState
        title="No activity yet"
        description="Sign-ins, lessons, identity checks and support requests will be listed here as they happen."
      />
    );
  } else {
    body = (
      <Stack spacing={3}>
        {groups.map((g) => (
          <Box key={g.key}>
            <Typography variant="h4" component="h3" sx={{ mb: 1 }}>
              {g.key}
            </Typography>
            <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {g.items.map((log, i) => (
                <ActivityItem key={log._id || `${g.key}-${i}`} log={log} />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      <PageHeader
        title="My activity"
        description={
          <>
            A record of what happens on your account: sign-ins, identity checks, lessons and support requests. If you
            see something you don't recognise,{" "}
            <Link component={RouterLink} to="/student/support">
              contact student support
            </Link>
            .
          </>
        }
      />

      <Section id="activity-log" title="Account activity">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2.5, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
        >
          <Typography role="status" sx={{ color: "text.secondary" }}>
            {!loading && !error && totalCount > 0 ? `Showing ${from}–${to} of ${totalCount} events, newest first` : ""}
          </Typography>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="activity-page-size-label">Events per page</InputLabel>
            <Select
              labelId="activity-page-size-label"
              id="activity-page-size"
              value={pageSize}
              label="Events per page"
              onChange={(e) => {
                dispatch(setLimit(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {body}

        {pages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={pages}
              page={Math.min(page, pages)}
              onChange={(_, p) => setPage(p)}
              color="primary"
              shape="rounded"
              aria-label="Activity pages"
              siblingCount={1}
            />
          </Box>
        )}
      </Section>
    </Box>
  );
}
